import { useState, useEffect, useRef, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { supabase } from "../../lib/supabase";
import { Label } from "../ui/label";
import { Info, Eye, Edit } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Button } from "../ui/button";

interface HtmlEditorProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  rows?: number;
  label?: string;
}

// Thêm CSS styles ở ngoài component
// Các styles này sẽ được áp dụng toàn cục
const customStyles = `
  .editor-container .ql-container {
    overflow-y: auto;
    border-bottom-left-radius: 0.375rem;
    border-bottom-right-radius: 0.375rem;
  }
  
  .editor-container .ql-toolbar {
    border-top-left-radius: 0.375rem;
    border-top-right-radius: 0.375rem;
    position: sticky; /* Make toolbar sticky */
    top: 0;           /* Stick to the top */
    background-color: white; /* Add background to prevent content showing through */
    z-index: 10;
    border-bottom: 1px solid #ccc; /* Add a separator */
  }

  /* Dark mode support for sticky toolbar */
  .dark .editor-container .ql-toolbar {
    background-color: #1a1a1a; /* Adjust dark mode background */
    border-bottom: 1px solid #2d3748;
  }
  
  /* Thêm khoảng cách dưới cùng để tránh đè lên nút */
  .editor-container {
    /* margin-bottom: 20px; Removed margin, handled by editor height/padding */
    position: relative; /* Ensure relative positioning for absolute children */
    z-index: 1; 
  }

  .ql-editor {
    overflow-y: auto;
    padding-bottom: 40px;
    /* Remove min-height if causing issues, height is set by style prop */
  }
  
  .preview-content {
    padding: 1rem;
    height: 100%;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
    background-color: white;
    box-sizing: border-box; /* Include padding/border in height */
  }
  
  .dark .preview-content {
    background-color: #1a1a1a;
    border-color: #2d3748;
  }
  
  .preview-content img {
    max-width: 100%;
    height: auto;
  }

  /* === CSS Fixes for Toolbar Dropdowns === */
  .editor-container .ql-toolbar .ql-picker {
    /* Ensure pickers don't cause excessive height */
    height: 24px; /* Match button height */
    display: inline-flex; /* Align items better */
    align-items: center;
  }

  .editor-container .ql-toolbar .ql-picker-label,
  .editor-container .ql-toolbar .ql-picker-options {
    z-index: 11; /* Ensure dropdowns are above the sticky toolbar */
    background-color: white; /* Background for dropdown */
    border: 1px solid #ccc;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    border-radius: 4px;
  }

  .editor-container .ql-toolbar .ql-picker-options {
    position: absolute; /* Position absolutely */
    /* Adjust positioning as needed */
    top: 100%; 
    left: 0;
    margin-top: 2px; /* Small gap */
  }

  /* Dark mode for dropdowns */
  .dark .editor-container .ql-toolbar .ql-picker-label,
  .dark .editor-container .ql-toolbar .ql-picker-options {
    background-color: #2d3748; 
    border-color: #4a5568;
    color: white;
  }
  /* === End CSS Fixes === */
`;

// Thêm style tag vào DOM khi module được load
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.innerHTML = customStyles;
  document.head.appendChild(styleElement);
}

const HtmlEditor = ({
  value,
  onChange,
  error,
  rows = 15,
  label = "Nội dung",
}: HtmlEditorProps) => {
  const quillRef = useRef<ReactQuill>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Hàm upload ảnh lên Supabase
  const uploadImageToSupabase = async (file: File): Promise<string> => {
    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Tạo tên file ngẫu nhiên để tránh trùng lặp
      const fileExt = file.name.split(".").pop();
      const fileName = `editor/${Math.random()
        .toString(36)
        .substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `articles/${fileName}`;

      // Upload ảnh lên Supabase Storage
      const { data, error } = await supabase.storage
        .from("images")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) throw error;

      // Lấy URL public cho ảnh đã upload
      const { data: publicUrlData } = supabase.storage
        .from("images")
        .getPublicUrl(filePath);

      return publicUrlData.publicUrl;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    } finally {
      setIsUploading(false);
      setUploadProgress(100);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  // Thiết lập các modules cho Quill
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }], // Headers
          [{ font: [] }], // Font family (sẽ dùng font mặc định của trình duyệt nếu không cấu hình thêm)
          [{ size: [] }], // Font size
          ["bold", "italic", "underline", "strike"], // Inline formatting
          [{ color: [] }, { background: [] }], // Color pickers
          [{ script: "sub" }, { script: "super" }], // Subscript/Superscript
          ["blockquote", "code-block"], // Block formats
          [{ list: "ordered" }, { list: "bullet" }], // Lists
          [{ indent: "-1" }, { indent: "+1" }], // Indentation
          [{ direction: "rtl" }, { align: [] }], // Text direction & alignment
          ["link", "image", "video"], // Embeds (video có thể cần xử lý thêm nếu muốn tùy chỉnh)
          ["clean"], // Remove formatting
        ],
        handlers: {
          image: function () {
            const input = document.createElement("input");
            input.setAttribute("type", "file");
            input.setAttribute("accept", "image/*");
            input.click();

            input.onchange = async () => {
              const file = input.files?.[0];
              if (!file) return;

              try {
                const url = await uploadImageToSupabase(file);
                const quill = quillRef.current?.getEditor();
                if (quill) {
                  const range = quill.getSelection();
                  if (range) {
                    quill.insertEmbed(range.index, "image", url);
                    quill.setSelection(range.index + 1, 0);
                  } else {
                    quill.insertEmbed(0, "image", url);
                    quill.setSelection(1, 0);
                  }
                }
              } catch (error) {
                console.error("Error inserting image:", error);
                alert("Không thể tải lên hình ảnh. Vui lòng thử lại.");
              }
            };
          },
          // Bạn có thể thêm handler cho 'video' nếu cần tùy chỉnh cách chèn video
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    []
  );

  // Thiết lập các định dạng cho Quill (cập nhật để bao gồm tất cả các tùy chọn toolbar)
  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "script",
    "blockquote",
    "code-block",
    "list",
    "bullet",
    "indent",
    "direction",
    "align",
    "link",
    "image",
    "video",
  ];

  // Thiết lập chiều cao cho editor
  const calculatedHeight = Math.min(rows * 21, 400); // Giới hạn chiều cao tối đa ở 400px thay vì 500px
  const editorStyle = {
    height: `${calculatedHeight}px`,
    maxHeight: "400px",
  };

  return (
    <div className="space-y-2 w-full">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <Label htmlFor="quill-editor" className="mr-2">
            {label} <span className="text-red-500">*</span>
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <Info className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs max-w-[250px]">
                  Sử dụng thanh công cụ phía trên để định dạng văn bản và chèn
                  hình ảnh. Nhấp vào biểu tượng hình ảnh để tải ảnh lên.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className="flex items-center gap-2">
          {isUploading && (
            <div className="flex items-center text-xs">
              <div className="mr-2 w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <span>Đang tải ảnh...</span>
            </div>
          )}

          <div className="flex items-center">
            <Button
              type="button"
              size="sm"
              variant={isPreviewMode ? "outline" : "default"}
              onClick={() => setIsPreviewMode(false)}
              className="rounded-r-none border-r-0 px-2 text-xs h-8"
              disabled={!value}
            >
              <Edit className="h-3.5 w-3.5 mr-1" />
              Soạn thảo
            </Button>
            <Button
              type="button"
              size="sm"
              variant={isPreviewMode ? "default" : "outline"}
              onClick={() => setIsPreviewMode(true)}
              className="rounded-l-none px-2 text-xs h-8"
              disabled={!value}
            >
              <Eye className="h-3.5 w-3.5 mr-1" />
              Xem trước
            </Button>
          </div>
        </div>
      </div>

      {/* Thêm class để xử lý z-index và height đảm bảo không đè lên các phần tử khác */}
      <div
        className={`${
          error ? "border border-red-500 rounded-md" : ""
        } relative editor-container`}
      >
        {isPreviewMode ? (
          <div
            className="preview-content"
            style={editorStyle}
            dangerouslySetInnerHTML={{
              __html: value || "<p>Chưa có nội dung</p>",
            }}
          />
        ) : (
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value}
            onChange={onChange}
            modules={modules}
            formats={formats}
            style={editorStyle}
            placeholder="Viết nội dung bài viết ở đây..."
            id="quill-editor"
          />
        )}
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
};

export default HtmlEditor;
